import React, { useMemo } from 'react';
import * as THREE from 'three';
import { Text } from '@react-three/drei';

interface MeasuringCylinderProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  liquidLevelMl?: number;
  liquidColor?: string;
  opacity?: number;
}

export function MeasuringCylinder({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  liquidLevelMl = 0,
  liquidColor = '#ffffff',
  opacity = 0.18,
}: MeasuringCylinderProps) {
  const radius = 0.05;
  const cylHeight = 0.45;
  const maxMl = 100;
  const fillH = (Math.max(0, Math.min(maxMl, liquidLevelMl)) / maxMl) * cylHeight * 0.9;
  const liquidBottomY = -cylHeight / 2 + 0.005;
  const liquidY = liquidBottomY + fillH / 2;

  const gradMarks = useMemo(() => {
    const marks: { vol: number; y: number }[] = [];
    for (let v = 10; v <= 100; v += 10) {
      marks.push({ vol: v, y: -cylHeight / 2 + (v / maxMl) * cylHeight * 0.9 });
    }
    return marks;
  }, []);

  const glass = (
    <meshPhysicalMaterial
      transparent
      opacity={opacity}
      roughness={0.02}
      reflectivity={0.9}
      ior={1.47}
      clearcoat={1}
      color="#dcf0ff"
      side={THREE.DoubleSide}
      depthWrite={false}
    />
  );

  return (
    <group position={position} rotation={rotation} scale={scale}>
      <group position={[0, cylHeight / 2, 0]}>
        {fillH > 0.005 && (
          <mesh position={[0, liquidY, 0]}>
            <cylinderGeometry args={[radius - 0.004, radius - 0.004, fillH, 48]} />
            <meshPhysicalMaterial
              color={liquidColor}
              transparent
              opacity={0.85}
              transmission={0.95}
              ior={1.33}
              roughness={0}
              depthWrite={false}
              side={THREE.DoubleSide}
            />
          </mesh>
        )}
        <mesh>
          <cylinderGeometry args={[radius, radius, cylHeight, 48, 1, true]} />
          {glass}
        </mesh>
        <mesh position={[0, -cylHeight / 2 + 0.002, 0]}>
          <cylinderGeometry args={[radius, radius, 0.005, 48]} />
          {glass}
        </mesh>
        <mesh position={[0, -cylHeight / 2 - 0.015, 0]}>
          <cylinderGeometry args={[radius * 1.6, radius * 1.6, 0.03, 48]} />
          {glass}
        </mesh>
        <mesh position={[radius - 0.005, cylHeight / 2 - 0.005, 0]} rotation={[0, 0, -0.4]}>
          <coneGeometry args={[0.012, 0.025, 16]} />
          {glass}
        </mesh>
        {gradMarks.map(({ vol, y }) => (
          <group key={vol} position={[0, y, 0]}>
            <mesh>
              <cylinderGeometry args={[radius + 0.0005, radius + 0.0005, 0.0012, 32, 1, true, -Math.PI / 4, Math.PI / 2]} />
              <meshBasicMaterial color="#ffffff" transparent opacity={0.85} side={THREE.DoubleSide} />
            </mesh>
            <Text
              position={[(radius + 0.012) * Math.sin(Math.PI / 4), 0, (radius + 0.012) * Math.cos(Math.PI / 4)]}
              fontSize={0.012}
              color="#ffffff"
              anchorX="left"
              anchorY="middle"
              fontWeight={600}
            >
              {vol}
            </Text>
          </group>
        ))}
        <Text
          position={[0, cylHeight / 2 - 0.04, radius + 0.001]}
          fontSize={0.018}
          color="#ffffff"
          anchorX="center"
          anchorY="middle"
          fontWeight={900}
          material-opacity={0.9}
        >
          100 ml
        </Text>
      </group>
    </group>
  );
}

export default MeasuringCylinder;
